import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'
import useReduxAction from '@hooks/useReduxAction'

import type { EntityOptions } from '@redux/modules/module'

import type { AgencyPortalModel } from '@models/types'

const watchEntityKeys = ['agencyPortals', 'portals']

type AgencyPortalFilters = {
  agency_id?: number,
  agency_title?: string,
  portal_id?: number,
}

type Options = {
  entityKey?: string,
  filters?: AgencyPortalFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
  sortKey?: string,
}

function useAgencyPortals(options: Options = {}) {
  const {
    entityKey, filters, requestOptions, sortKey,
  } = options
  const {
    agency_id: filterAgencyId,
    agency_title: filterAgencyTitle,
    portal_id: filterPortalId,
  } = filters || {}

  const {
    updatedEntities: { agencyPortals: agencyPortalsUpdatedAt, portals: portalsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const {
    agencies,
    agencyPortals,
    portals,
  } = useSelector(reduxState => reduxState.entities)

  const filteredAgencyPortals = useMemo((): AgencyPortalModel[] => {
    const filtered = Object.values(agencyPortals).filter((agencyPortal) => {
      const { agency_id, portal_id } = agencyPortal
      const agencyTitle = agencies[agency_id]?.title

      const matchAgencyId: boolean = matchFilterNumber(agency_id, filterAgencyId)
      const matchPortalId: boolean = matchFilterNumber(portal_id, filterPortalId)

      // Match string filter
      const matchAgencyStringId = agency_id?.toString() === filterAgencyTitle
      const matchAgencyStringTitle = matchFilterString(agencyTitle, filterAgencyTitle)
      const matchAgencyString = matchAgencyStringId || matchAgencyStringTitle

      return matchAgencyId && matchAgencyString && matchPortalId
    })

    const sorted = sortKey === 'agencyTitle'
      ? sortArrayBy(filtered, 'asc', agencyPortal => agencies[agencyPortal.agency_id]?.title)
      : sortArrayBy(filtered, 'asc', agencyPortal => portals[agencyPortal.portal_id]?.title)

    return sorted
  }, [agencyPortalsUpdatedAt, portalsUpdatedAt, JSON.stringify(filters)])

  const filteredAgencyPortalsCount: number = filteredAgencyPortals.length
  const hasFilteredAgencyPortals: boolean = !!filteredAgencyPortalsCount
  const assignedPortalIds: number[] = filteredAgencyPortals.map(agencyPortal => agencyPortal.portal_id)

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAgencyPortalsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingAgencyPortals } = useReduxAction(
    'agencyPortals',
    'loadAgencyPortals',
    {
      entityKey,
      ...filtersWithOffset,
      ...requestOptions,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
    },
  )

  return {
    assignedPortalIds,
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyPortals,
    filteredAgencyPortalsCount,
    hasFilteredAgencyPortals,
    loading: !!loadingAgencyPortals,
  }
}

export default useAgencyPortals
